import React, { useEffect, useState } from 'react';
import { Container, Card, Button, ListGroup, Badge, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FaBell, FaCalendarAlt, FaComment, FaInfoCircle, FaBellSlash, FaTrash, FaCheck } from 'react-icons/fa';
import { notificationsAPI } from '../api/apiClient';
import { formatDistanceToNow } from 'date-fns';
import { de, enUS } from 'date-fns/locale';

export default function Notifications() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await notificationsAPI.index();
      if (response.success) {
        const notificationsData =
          response.data?.data?.notifications?.data ||
          response.data?.data ||
          [];

        setNotifications(Array.isArray(notificationsData) ? notificationsData : []);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const handleMarkAsRead = async (id) => {
    try {
      const response = await notificationsAPI.markAsRead(id);
      if (response.success) {
        setNotifications(notifications.map(n => 
          n.id === id ? { ...n, read_at: new Date().toISOString() } : n
        ));
      }
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await notificationsAPI.delete(id);
      if (response.success) {
        setNotifications(notifications.filter(n => n.id !== id));
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const response = await notificationsAPI.markAllAsRead();
      if (response.success) {
        setNotifications(notifications.map(n => ({ ...n, read_at: new Date().toISOString() })));
      }
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleNotificationClick = (notification) => {
    const { id, data } = notification;
    const type = data?.type;

    if (!notification.read_at) {
      handleMarkAsRead(id);
    }

    const booking_id = data?.booking_id || (type === 'new_booking' ? data?.id : null);
    const transport_id = data?.transport_id || (type === 'new_transport' ? data?.id : null);
    const order_id = data?.order_id;
    const invoice_id = data?.invoice_id;
    const customer_id = data?.customer_id;
    const driver_id = data?.driver_id;
    const route = data?.route;

    // Redirection logic based on type and provided IDs
    if (type === 'chat' || data?.chat_id) {
      if (customer_id) {
        navigate(`/admin/client-list/chat-client/${customer_id}`);
      } else {
        navigate('/admin/client-list');
      }
    } 
    // Booking Orders (Requests)
    else if (type === 'price_proposal' || type === 'order_received' || type === 'order_delivered' || type === 'price_approved' || type === 'price_rejected') {
      if (order_id) {
        navigate(`/admin/booking-orders/details/${order_id}`);
      } else {
        navigate('/admin/booking-orders');
      }
    }
    // Transport Orders (Requests)
    else if (type === 'transport_offer' || type === 'transport_received' || type === 'transport_delivered') {
      if (order_id) {
        navigate(`/admin/transport-orders/details/${order_id}`);
      } else {
        navigate('/admin/transport-orders');
      }
    }
    // Bookings
    else if (type === 'new_booking' || type === 'booking_approved' || type === 'booking_rejected' || booking_id) {
      if (booking_id && !isNaN(booking_id)) {
        navigate(`/admin/bookings/details/${booking_id}`);
      } else {
        navigate('/admin/bookings');
      }
    }
    // Transports
    else if (type === 'new_transport' || transport_id) {
      if (transport_id && !isNaN(transport_id)) {
        navigate(`/admin/transports/details/${transport_id}`);
      } else {
        navigate('/admin/transports');
      }
    }
    // Invoices
    else if (type === 'invoice_reminder') {
      if (invoice_id) {
        navigate(`/admin/invoices/edit/${invoice_id}`);
      } else {
        navigate('/admin/invoices');
      }
    }
    // Driver-centric notifications
    else if (type === 'new_vehicle' || type === 'document_expiration' || type === 'driver_deleted_account' || type === 'driver_profile_updated') {
      if (driver_id) {
        navigate(`/admin/drivers/details/${driver_id}`);
      } else if (route) {
        navigate(route);
      } else {
        navigate('/admin/drivers');
      }
    }
    else {
      // Default fallback
      // navigate('/admin/notifications');
    }
  };

  const getTimeAgo = (date) => {
    try {
      return formatDistanceToNow(new Date(date), { 
        addSuffix: true, 
        locale: i18n.language === 'de' ? de : enUS 
      });
    } catch (e) {
      return '';
    }
  };

  return (
    <Container className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="m-0 fw-bold d-flex align-items-center">
          <FaBell className="text-primary me-2" />
          {t('notifications')}
        </h4>
        {notifications.length > 0 && (
          <Button variant="outline-primary" size="sm" onClick={handleMarkAllAsRead}>
            <FaCheck className="me-1" /> {t('mark_all_read')}
          </Button>
        )}
      </div>

      <Card className="shadow-sm border-0 rounded-3">
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : notifications.length > 0 ? (
            <ListGroup variant="flush">
              {notifications.map((notification) => (
                <ListGroup.Item 
                  key={notification.id} 
                  className={`p-4 border-bottom ${!notification.read_at ? 'bg-light' : ''}`}
                >
                  <div className="d-flex align-items-start gap-3">
                    <div className="notification-icon-wrapper p-3 rounded-circle bg-white shadow-sm border">
                      {notification.data?.type === 'chat' ? (
                        <FaComment className="text-info" size={20} />
                      ) : notification.data?.type?.includes('booking') ? (
                        <FaCalendarAlt className="text-primary" size={20} />
                      ) : (
                        <FaInfoCircle className="text-secondary" size={20} />
                      )}
                    </div>
                    <div className="flex-grow-1">
                      <div className="d-flex justify-content-between align-items-start mb-1">
                        <div>
                          <h6 className="mb-1 fw-bold text-dark">
                            {notification.data?.title || t('notification')}
                            {!notification.read_at && <Badge bg="primary" pill className="ms-2" style={{ fontSize: '10px' }}>New</Badge>}
                          </h6>
                          <p className="mb-2 text-muted">
                            {notification.data?.body}
                          </p>
                          <span className="text-muted small d-block mb-3">
                            {getTimeAgo(notification.created_at)}
                          </span>
                        </div>
                      </div>
                      <div className="d-flex gap-2">
                        <Button 
                          variant="primary" 
                          size="sm" 
                          className="rounded-pill px-4"
                          onClick={() => handleNotificationClick(notification)}
                        >
                          {t('view_details')}
                        </Button>
                        {!notification.read_at && (
                          <Button 
                            variant="light" 
                            size="sm" 
                            className="rounded-pill"
                            onClick={() => handleMarkAsRead(notification.id)}
                          >
                            <FaCheck className="me-1" /> {t('mark_as_read')}
                          </Button>
                        )}
                        <Button 
                          variant="outline-danger" 
                          size="sm" 
                          className="rounded-pill"
                          onClick={() => handleDelete(notification.id)}
                        >
                          <FaTrash />
                        </Button>
                      </div>
                    </div>
                  </div>
                </ListGroup.Item>
              ))}
            </ListGroup>
          ) : (
            <div className="text-center py-5">
              <FaBellSlash size={50} className="text-muted mb-3 opacity-25" />
              <h5 className="text-muted">{t('no_notifications')}</h5>
              <p className="text-muted small mb-0">Check back later for new updates.</p>
            </div>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
}
